import DmpDocumentMetadata from "cnam/models/dmp-document-metadata";
import { DocumentStatus, DocumentVisibility, InsOid } from "cnam/types/types";
import dmpDocumentDate from "cnam/utils/dmp-document-date";
import ControlTower from "wise-control-tower";
import { DmpAdapter } from "../dmp-adapter";
// We need to hardcore this value because hl_getMajorityAge from DMP-Connect
// needs the connector to be created (which is not the case in this step)
const MAJORITY_AGE = 18;
export default class DmpConnectJSDmpAdapter extends DmpAdapter {
  // observeCard = task(async () => {
  //   console.log("not implemented for dmpconnectjs");
  // });

  constructor(dmpConnect, cardReaderAdapter, authenticationContext, medic, store) {
    super(medic);
    this.dmpConnect = dmpConnect;
    this.cardReaderAdapter = cardReaderAdapter;
    this.authenticationContext = authenticationContext;
    this.store = store;
  }
  async shouldSetSecretConnection(patient) {
    return patient.patientAge < MAJORITY_AGE;
  }
  async checkAvailability() {
    await this.dmpConnect.registerConfiguration();
  }
  async readCpsCard() {
    try {
      return await this.cardReaderAdapter.getCpsInformations();
    } catch (error) {
      await this.dmpConnect.closeSession();
      if (!this.cardReaderAdapter.isCardError(error)) {
        ControlTower.captureMessage(`CnamApiError: ${error.message}. ${JSON.stringify(error.error)}`);
      }

      // Errors must be handled by the caller
      throw error;
    }
  }
  async readVitaleCard() {
    try {
      const response = await this.cardReaderAdapter.getVitaleInformations();
      return response;
    } catch (error) {
      if (!this.cardReaderAdapter.isCardError(error)) {
        ControlTower.captureMessage(`CnamApiError: ${error.message}. ${JSON.stringify(error.error)}`);
      }

      // Errors must be handled by the caller
      throw error;
    }
  }
  async createConnector() {
    await this.dmpConnect.createConnector(this.authenticationContext.cpsPracticeLocationIndice, this.authenticationContext.availableLocations);
  }
  openSession() {
    const selectedHealthCentre = this.medic.healthCentres.find(hc => hc.finess === this.authenticationContext.computerLocation);
    return this.dmpConnect.openSession(selectedHealthCentre?.dmpHealthCentreConfig, this.dmpConnect.windowsUserForMedic(this.medic));
  }
  closeSession() {
    return this.dmpConnect.closeSession();
  }
  async setConfidentialityLevel(secretConnection) {
    await this.dmpConnect.setConfidentialityLevel(secretConnection);
  }
  async td02Exist(ins, secretConnection = false) {
    await this.setConfidentialityLevel(secretConnection);
    return this.dmpConnect.getDirectAuthenticationDMPStatus(this._insToDmpConnectJSNir(ins));
  }
  async td03AddAuthorization(ins, secretConnection = false) {
    await this.setConfidentialityLevel(secretConnection);
    return this.dmpConnect.updateDmpAccessAuthorization(this._insToDmpConnectJSNir(ins));
  }
  async td31Find(ins, criteria, secretConnection = false) {
    await this.setConfidentialityLevel(secretConnection);
    const dmpConnectJSCriteria = this._toDmpConnectJSCriteria(criteria);
    const documents = await this.dmpConnect.findDocuments(this._insToDmpConnectJSNir(ins), dmpConnectJSCriteria);
    return documents.map(doc => {
      return this.dmpDocumentMetadataFromDmpPayload(doc);
    }).filter(Boolean);
  }
  async td32RetrieveContent(ins, dmpDocumentMetadata, secretConnection = false) {
    await this.setConfidentialityLevel(secretConnection);
    const parameters = {
      i_getCdaHeaders: true,
      i_getCdaContent: true
    };
    const response = await this.dmpConnect.getDocumentContentByUniqueId(this._insToDmpConnectJSNir(ins), dmpDocumentMetadata.uniqueId, dmpDocumentMetadata.uuid, parameters);
    const dmpDocumentContent = {
      body: response.s_fileContentInBase64,
      bodyMimeType: this._computeMimeTypeFromDocumentFormat(response.i_documentFormat),
      cdaHeaders: response.s_cdaHeadersInBase64,
      cdaHeadersMimeType: "text/html",
      cdaContent: response.s_cdaContentInBase64,
      title: response.s_documentFormatDescription
    };
    return dmpDocumentContent;
  }
  async td09GetWebPSURLs(ins, secretConnection = false) {
    await this.setConfidentialityLevel(secretConnection);
    return this.dmpConnect.getWebPsDmpUrls(this._insToDmpConnectJSNir(ins));
  }
  _insToDmpConnectJSNir(ins) {
    let suffix = "";
    switch (ins.ins.oid) {
      case InsOid.DEMO:
        suffix = "D";
        break;
      case InsOid.TEST:
        suffix = "T";
        break;
      case InsOid.NIA:
        suffix = "A";
        break;
    }
    return `${ins.ins.value}${suffix}`;
  }

  /**
   * Returns a bit mask representing the status displayed
   */
  _computeStatusBitMask(statuses) {
    // Initialize to 1 because it is the "normal" status
    // let bitFilters = 1;
    let bitFilters = 0;
    if (statuses.includes(DocumentStatus.ARCHIVED)) bitFilters = bitFilters | DMPConnect.DocumentStatus.Archived;
    if (statuses.includes(DocumentStatus.DEPRECATED)) bitFilters = bitFilters | DMPConnect.DocumentStatus.Deprecated;
    return bitFilters;
  }

  /**
   * Returns A bit mask representing the visibility displayed.
   */
  _computeVisibilityBitMask(confidentialities) {
    // Initialize to 1 because it is the "normal" visibility
    let bitFilters = 1;
    if (confidentialities.includes(DocumentVisibility.HIDDEN_TO_PATIENT)) {
      bitFilters = bitFilters | DMPConnect.DocumentVisibility.PatientHidden;
    }
    if (confidentialities.includes(DocumentVisibility.HIDDEN_TO_HEALTHCARE_PROFESSIONAL)) {
      bitFilters = bitFilters | DMPConnect.DocumentVisibility.HealthcareProfesionnalHidden;
    }
    if (confidentialities.includes(DocumentVisibility.HIDDEN_TO_LEGAL_REPRESENTATIVES)) {
      bitFilters = bitFilters | DMPConnect.DocumentVisibility.GuardianHidden;
    }
    return bitFilters;
  }
  _computeMimeTypeFromDocumentFormat(documentFormat) {
    switch (documentFormat) {
      case DMPConnect.DocumentFormat.RtfText:
        return "application/rtf";
      case DMPConnect.DocumentFormat.JpgImage:
        return "image/jpeg";
      case DMPConnect.DocumentFormat.TiffImage:
        return "image/tiff";
      case DMPConnect.DocumentFormat.PdfApplication:
        return "application/pdf";
      case DMPConnect.DocumentFormat.CrBiologie:
      case DMPConnect.DocumentFormat.VsmDocument:
        return "text/html";
      case DMPConnect.DocumentFormat.PlainText:
      default:
        return "text/html";
    }
  }
  _toDmpConnectJSCriteria(criteria) {
    const result = {};
    if (criteria.typeCodes) {
      result.Categories = criteria.typeCodes.reduce(function (accumulator, typeCode, index) {
        accumulator[`#${index}`] = typeCode;
        return accumulator;
      }, {});
    }
    if (criteria.serviceStartTimeFrom) {
      result.s_serviceStartDateBottom = dmpDocumentDate.dateToStr(criteria.serviceStartTimeFrom);
    }
    if (criteria.serviceStopTimeTo) {
      result.s_serviceStartDateTop = dmpDocumentDate.dateToStr(criteria.serviceStopTimeTo);
    }
    if (criteria.submissionStartTimeFrom) {
      result.s_submissionDateBottom = dmpDocumentDate.dateToStr(criteria.submissionStartTimeFrom);
    }
    if (criteria.statuses) {
      result.i_status = this._computeStatusBitMask(criteria.statuses);
    }
    if (criteria.visibilities) {
      result.i_visibility = this._computeVisibilityBitMask(criteria.visibilities);
    }
    return result;
  }
  _computeDocumentVisibilityFromDocumentVisibility(documentVisibility) {
    switch (documentVisibility) {
      case DMPConnect.DocumentVisibility.Normal:
        return DocumentVisibility.NORMAL;
      case DMPConnect.DocumentVisibility.PatientHidden:
        return DocumentVisibility.HIDDEN_TO_PATIENT;
      case DMPConnect.DocumentVisibility.HealthcareProfesionnalHidden:
        return DocumentVisibility.HIDDEN_TO_HEALTHCARE_PROFESSIONAL;
      case DMPConnect.DocumentVisibility.GuardianHidden:
        return DocumentVisibility.HIDDEN_TO_LEGAL_REPRESENTATIVES;
      default:
        return DocumentVisibility.NORMAL;
    }
  }
  _computeStatusFromDocumentStatus(documentStatus) {
    switch (documentStatus) {
      case DMPConnect.DocumentStatus.Approved:
        return DocumentStatus.APPROVED;
      case DMPConnect.DocumentStatus.Deprecated:
        return DocumentStatus.DEPRECATED;
      case DMPConnect.DocumentStatus.Archived:
        return DocumentStatus.ARCHIVED;
      default:
        return DocumentStatus.APPROVED;
    }
  }
  dmpDocumentMetadataFromDmpPayload(payload) {
    const authors = payload.Authors.map(author => ({
      institution: {
        classCode: author.Institution.s_classCode,
        id: author.Institution.s_id,
        name: author.Institution.s_name
      },
      handle: author.i_handle,
      hpAuthenticationMode: author.i_hpAuthenticationMode,
      hpInternalIdType: author.i_hpInternalIdType,
      hpGiven: author.s_hpGiven,
      hpInstitution: author.s_hpInstitution,
      hpInternalId: author.s_hpInternalId,
      hpName: author.s_hpName,
      hpProfession: author.s_hpProfession,
      hpProfessionDescription: author.s_hpProfessionDescription,
      hpProfessionOid: author.s_hpProfessionOid,
      hpSpeciality: author.s_hpSpeciality,
      hpSpecialityDescription: author.s_hpSpecialityDescription
    }));
    return new DmpDocumentMetadata({
      authors,
      eventCodes: payload.EventCodes,
      documentFormat: payload.i_document_Format,
      documentStatus: payload.i_document_Status,
      documentVisibility: payload.i_document_Visibility,
      handle: payload.i_handle,
      size: payload.i_size,
      classCode: payload.s_classCode,
      creationDate: payload.s_creationDate,
      description: payload.s_description,
      documentFormatCode: payload.s_documentFormatCode,
      documentFormatDescription: payload.s_documentFormatDescription,
      hashSha1: payload.s_hashSha1,
      healthCareFacilityTypeCode: payload.s_healthCareFacilityTypeCode,
      nextUUId: payload.s_nextUUId,
      practiceSettingCode: payload.s_practiceSettingCode,
      previousUUId: payload.s_previousUUId,
      serviceStartDate: payload.s_serviceStartDate,
      serviceStopDate: payload.s_serviceStopDate,
      submissionDate: payload.s_submissionDate,
      submissionSetId: payload.s_submissionSetId,
      title: payload.s_title,
      type: payload.s_typeCode,
      uniqueId: payload.s_uniqueId,
      uuid: payload.s_uuid,
      confidentialities: payload.i_document_Visibility ? [this._computeDocumentVisibilityFromDocumentVisibility(payload.i_document_Visibility)] : [],
      creationTime: payload.s_creationDate ? dmpDocumentDate.strToDate(payload.s_creationDate) : undefined,
      mimeType: payload.i_document_Format ? this._computeMimeTypeFromDocumentFormat(payload.i_document_Format) : undefined,
      format: payload.i_document_Format?.toString(),
      formatDescription: payload.s_documentFormatDescription?.toString(),
      status: payload.i_document_Status ? [this._computeStatusFromDocumentStatus(payload.i_document_Status)] : []
    });
  }
}